/**
 * Système de monitoring et logging enterprise-grade
 * Logs structurés, métriques, et alertes
 */

import { captureError, captureMessage, addBreadcrumb, setTag } from './sentry';
import { logger } from './logger';

// Types de logs
export enum LogLevel {
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error',
  FATAL = 'fatal',
}

// Types d'événements métier
export enum EventType {
  // Inscription
  INSCRIPTION_STARTED = 'inscription.started',
  INSCRIPTION_STEP_COMPLETED = 'inscription.step_completed',
  INSCRIPTION_COMPLETED = 'inscription.completed',
  INSCRIPTION_ABANDONED = 'inscription.abandoned',

  // Questionnaire
  QUESTIONNAIRE_STARTED = 'questionnaire.started',
  QUESTIONNAIRE_COMPLETED = 'questionnaire.completed',
  LEVEL_DETECTED = 'level.detected',

  // Paiement
  PAYMENT_INITIATED = 'payment.initiated',
  PAYMENT_SUCCESS = 'payment.success',
  PAYMENT_FAILED = 'payment.failed',
  PAYMENT_CANCELLED = 'payment.cancelled',

  // Formation
  COURSE_VIEWED = 'course.viewed',
  COURSE_ENROLLED = 'course.enrolled',
  MODULE_COMPLETED = 'module.completed',

  // Erreurs critiques
  CRITICAL_ERROR = 'error.critical',
  API_ERROR = 'error.api',
  DATABASE_ERROR = 'error.database',
}

interface LogContext {
  userId?: string;
  courseId?: string;
  inscriptionId?: string;
  sessionId?: string;
  [key: string]: any;
}

class MonitoringService {
  private sessionId: string;
  private environment: string;

  constructor() {
    this.sessionId = this.generateSessionId();
    this.environment = import.meta.env.VITE_ENVIRONMENT || import.meta.env.MODE;
  }

  /**
   * Générer un ID de session unique
   */
  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substring(7)}`;
  }

  /**
   * Logger un événement structuré
   */
  log(
    level: LogLevel,
    message: string,
    context?: LogContext
  ): void {
    const logEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      sessionId: this.sessionId,
      environment: this.environment,
      ...context,
    };

    // Console en développement
    if (this.environment === 'development') {
      const emoji = this.getEmoji(level);
      console.log(
        `${emoji} [${level.toUpperCase()}]`,
        message,
        context || ''
      );
    }

    // Envoyer à Sentry tous les logs en production (maintenant que Logs est activé)
    if (this.environment === 'production') {
      if (level === LogLevel.ERROR || level === LogLevel.FATAL) {
        captureMessage(message, level);
      } else if (level === LogLevel.WARN) {
        captureMessage(message, 'warning');
      } else if (level === LogLevel.INFO) {
        captureMessage(message, 'info');
      }
      // Toujours ajouter un breadcrumb pour le contexte
      addBreadcrumb(message, context);
    } else if (level === LogLevel.ERROR || level === LogLevel.FATAL || level === LogLevel.WARN) {
      // En développement, envoyer uniquement les erreurs/warnings critiques
      addBreadcrumb(message, context);
    }

    // Logs structurés pour analyse (peut être envoyé à DataDog, LogRocket, etc.)
    this.sendToLogAggregator(logEntry);
  }

  /**
   * Tracker un événement métier
   */
  trackEvent(
    eventType: EventType,
    properties?: Record<string, any>
  ): void {
    const event = {
      event: eventType,
      timestamp: new Date().toISOString(),
      sessionId: this.sessionId,
      properties: properties || {},
    };

    // Log l'événement
    this.log(LogLevel.INFO, `Event: ${eventType}`, properties);

    // Breadcrumb Sentry
    addBreadcrumb(eventType, properties);

    // Analytics (Google Analytics, Mixpanel, etc.)
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', eventType, properties);
    }

    // Tags Sentry pour filtrage
    if (properties?.userId) {
      setTag('user_id', properties.userId);
    }
    if (properties?.courseId) {
      setTag('course_id', properties.courseId);
    }
  }

  /**
   * Mesurer la performance d'une opération
   */
  async measurePerformance<T>(
    operationName: string,
    operation: () => Promise<T>,
    context?: Record<string, any>
  ): Promise<T> {
    const startTime = performance.now();

    try {
      const result = await operation();
      const duration = performance.now() - startTime;

      this.log(LogLevel.INFO, `Performance: ${operationName}`, {
        duration: `${duration.toFixed(2)}ms`,
        success: true,
        ...context,
      });

      // Alerter si l'opération est lente (> 3s)
      if (duration > 3000) {
        this.log(LogLevel.WARN, `Slow operation: ${operationName}`, {
          duration: `${duration.toFixed(2)}ms`,
          ...context,
        });
      }

      return result;
    } catch (error) {
      const duration = performance.now() - startTime;

      this.log(LogLevel.ERROR, `Performance error: ${operationName}`, {
        duration: `${duration.toFixed(2)}ms`,
        error: error instanceof Error ? error.message : String(error),
        ...context,
      });

      throw error;
    }
  }

  /**
   * Tracker les Web Vitals (Core Web Vitals)
   */
  trackWebVitals(): void {
    if (typeof window === 'undefined') return;

    // Import dynamique pour éviter le bundle en dev
    import('web-vitals').then(({ onCLS, onINP, onFCP, onLCP, onTTFB }) => {
      onCLS((metric) => this.reportWebVital(metric));
      onINP((metric) => this.reportWebVital(metric));
      onFCP((metric) => this.reportWebVital(metric));
      onLCP((metric) => this.reportWebVital(metric));
      onTTFB((metric) => this.reportWebVital(metric));
    }).catch(err => {
      logger.warn('Web Vitals not available', { error: err?.message });
    });
  }

  /**
   * Reporter une métrique Web Vital
   */
  private reportWebVital(metric: any): void {
    this.log(LogLevel.INFO, `Web Vital: ${metric.name}`, {
      value: metric.value,
      rating: metric.rating,
      delta: metric.delta,
    });

    // Envoyer à Google Analytics
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', metric.name, {
        value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
        event_category: 'Web Vitals',
        event_label: metric.id,
        non_interaction: true,
      });
    }
  }

  /**
   * Envoyer les logs à un agrégateur (DataDog, LogRocket, etc.)
   */
  private sendToLogAggregator(logEntry: any): void {
    // En production, envoyer à votre service de logs
    if (this.environment === 'production') {
      // Exemple avec fetch vers un endpoint de logs
      // fetch('/api/logs', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(logEntry),
      // }).catch(() => {});
    }
  }

  /**
   * Emoji pour les logs console
   */
  private getEmoji(level: LogLevel): string {
    const emojis = {
      [LogLevel.DEBUG]: '',
      [LogLevel.INFO]: 'ℹ️',
      [LogLevel.WARN]: '️',
      [LogLevel.ERROR]: '',
      [LogLevel.FATAL]: '💀',
    };
    return emojis[level] || 'ℹ️';
  }

  /**
   * Logger les erreurs non catchées
   */
  setupGlobalErrorHandlers(): void {
    if (typeof window === 'undefined') return;

    // Erreurs JavaScript non catchées
    window.addEventListener('error', (event) => {
      this.log(LogLevel.ERROR, 'Uncaught error', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack,
      });

      captureError(event.error || new Error(event.message));
    });

    // Promesses rejetées non catchées
    window.addEventListener('unhandledrejection', (event) => {
      this.log(LogLevel.ERROR, 'Unhandled promise rejection', {
        reason: event.reason,
      });

      const error = event.reason instanceof Error
        ? event.reason
        : new Error(String(event.reason));

      captureError(error);
    });
  }
}

// Instance singleton
export const monitoring = new MonitoringService();

// Export des helpers
export const log = {
  debug: (message: string, context?: LogContext) =>
    monitoring.log(LogLevel.DEBUG, message, context),

  info: (message: string, context?: LogContext) =>
    monitoring.log(LogLevel.INFO, message, context),

  warn: (message: string, context?: LogContext) =>
    monitoring.log(LogLevel.WARN, message, context),

  error: (message: string, context?: LogContext) =>
    monitoring.log(LogLevel.ERROR, message, context),

  fatal: (message: string, context?: LogContext) =>
    monitoring.log(LogLevel.FATAL, message, context),
};

export const track = {
  event: (eventType: EventType, properties?: Record<string, any>) =>
    monitoring.trackEvent(eventType, properties),

  performance: <T>(name: string, operation: () => Promise<T>, context?: Record<string, any>) =>
    monitoring.measurePerformance(name, operation, context),
    
  action: (action: string, metadata?: Record<string, any>) =>
    monitoring.log(LogLevel.INFO, `User Action: ${action}`, metadata),
    
  pageView: (path: string, metadata?: Record<string, any>) =>
    monitoring.log(LogLevel.INFO, `Page View: ${path}`, { path, ...metadata }),
    
  error: (error: Error, componentStack?: string) =>
    monitoring.log(LogLevel.ERROR, `Error: ${error.message}`, { 
      name: error.name, 
      stack: error.stack,
      componentStack 
    }),
};

export const perf = {
  startTimer: (name: string): (() => number) => {
    const start = performance.now();
    return () => {
      const duration = Math.round(performance.now() - start);
      monitoring.log(LogLevel.DEBUG, `Timer ${name}: ${duration}ms`, { duration });
      return duration;
    };
  },
  
  recordMetric: (name: string, value: number, unit: string, tags?: Record<string, string>) => {
    monitoring.log(LogLevel.INFO, `Metric: ${name} = ${value}${unit}`, { name, value, unit, tags });
  },
};
